import { useCallback, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { successToast, errorToast } from 'utils/toastUtils';

/**
 * Serializes an editable field's saves per selected user through a promise
 * queue, so an overlapping edit and a background refetch can't clobber each
 * other. A generation counter discards a stale save's rollback once a newer
 * edit has started, and `dirtyRef` protects an unsaved edit from being wiped
 * by `reseed` while the user is still typing.
 *
 * @param {*} emptyValue value used before the server value is known
 * @return {object} `value`/`setValue`/`errors`/`setErrors` state, the refs a
 *   field's onBlur/onChange handlers read directly (`dirtyRef`,
 *   `requestedRef`), and `save`/`reseed`
 */
export function useQueuedFieldSave(emptyValue) {
  const intl = useIntl();
  const [value, setValue] = useState(emptyValue);
  const [errors, setErrors] = useState({});

  const savedRef = useRef(emptyValue);
  const requestedRef = useRef(emptyValue);
  const dirtyRef = useRef(false);
  const pendingSavesRef = useRef(0);
  const saveQueueRef = useRef(Promise.resolve());
  const saveGenerationRef = useRef(0);
  const seededForUserIdRef = useRef(null);

  /**
   * Queues `apiCall(next)` behind any save already in flight for this field,
   * applying `next` optimistically. On failure, rolls back to `previous` (or
   * the last saved value if `previous` isn't given) unless a newer edit is
   * already in flight for the same user, or the field has since gone dirty.
   *
   * @param {Function} apiCall (next) => Promise, the request to send
   * @param {*} next the optimistic value to apply and send
   * @param {object} opts
   * @param {string|number} opts.userId the user this save belongs to
   * @param {*} [opts.previous] rollback target on failure (default: last save)
   * @param {Function} [opts.refetchNetwork] called after a successful save
   * @return {Promise<boolean>} whether the save succeeded
   */
  const save = useCallback(
    (apiCall, next, { userId, previous, refetchNetwork } = {}) => {
      const generation = ++saveGenerationRef.current;
      const stillSameUser = () => seededForUserIdRef.current === userId;

      requestedRef.current = next;
      dirtyRef.current = false;
      pendingSavesRef.current += 1;
      setValue(next);

      const runSave = () =>
        apiCall(next)
          .then(() => {
            if (stillSameUser()) savedRef.current = next;
            successToast({ body: 'USER_OBJECTS.CONFIG_UPDATE_SUCCESS', intl });
            if (refetchNetwork) refetchNetwork();
            return true;
          })
          .catch((err) => {
            if (stillSameUser() && generation === saveGenerationRef.current) {
              const rollbackTo = previous !== undefined ? previous : savedRef.current;
              requestedRef.current = rollbackTo;
              if (!dirtyRef.current) setValue(rollbackTo);
            }
            errorToast({ body: err.response?.data?.error || err.message, intl });
            return false;
          })
          .finally(() => {
            pendingSavesRef.current -= 1;
          });

      saveQueueRef.current = saveQueueRef.current.then(runSave, runSave);
      return saveQueueRef.current;
    },
    [intl]
  );

  /**
   * Seeds `value` from the server once per user. Skips reseeding when the
   * user hasn't changed and there's an unsaved edit or a save in flight, so
   * an unrelated refetch can't discard it.
   *
   * @param {*} serverValue the freshly fetched value for the current user
   * @param {string|number} userId the currently selected user's id
   */
  const reseed = useCallback((serverValue, userId) => {
    const userChanged = seededForUserIdRef.current !== userId;
    const hasLocalWork = dirtyRef.current || pendingSavesRef.current > 0;
    if (!userChanged && hasLocalWork) return;

    if (userChanged) saveGenerationRef.current += 1;
    seededForUserIdRef.current = userId;
    savedRef.current = serverValue;
    requestedRef.current = serverValue;
    dirtyRef.current = false;
    setValue(serverValue);
    setErrors({});
  }, []);

  return {
    value,
    setValue,
    errors,
    setErrors,
    dirtyRef,
    requestedRef,
    pendingSavesRef,
    seededForUserIdRef,
    save,
    reseed,
  };
}
